package com.trevorwiebe.timesheet.loads.presentation.admin

import com.trevorwiebe.timesheet.admin.loadrate.domain.model.LoadRate
import com.trevorwiebe.timesheet.admin.loadrate.domain.model.LoadRateCategory
import com.trevorwiebe.timesheet.admin.loadrate.domain.model.LoadRateType
import com.trevorwiebe.timesheet.admin.loadrate.domain.model.LoadRateUnit
import com.trevorwiebe.timesheet.admin.loadrate.domain.model.displayName
import com.trevorwiebe.timesheet.admin.loadrate.domain.model.poundsPerUnit
import com.trevorwiebe.timesheet.core.domain.centsToDollarInput
import com.trevorwiebe.timesheet.core.domain.formatCentsAsDollars
import com.trevorwiebe.timesheet.core.domain.dollarsToCents
import com.trevorwiebe.timesheet.loads.domain.model.LoadAttachment
import com.trevorwiebe.timesheet.loads.domain.model.LoadRateInfo
import com.trevorwiebe.timesheet.mileage.domain.model.Location
import kotlin.math.abs
import kotlin.math.roundToLong
import kotlin.random.Random
import kotlin.time.Clock
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime

/** Today in the device's timezone — the default date for a freshly-started load. */
internal fun todayDate(): LocalDate =
    Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date

private const val ID_CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789"

/**
 * Document id for a load that hasn't been saved yet, fixed when the form opens. A save that times
 * out still lands later, so a retry has to write the same document rather than add a second load.
 */
internal fun newLoadDraftId(): String = (1..20).map { ID_CHARS[Random.nextInt(ID_CHARS.length)] }.joinToString("")

/**
 * One pickup or drop stop in the add-load form. Holds a location selection (an existing id or a
 * brand-new location entered inline — the same shape the mileage location picker uses). A load can
 * have any number of these on each side; the load's date is shared across all of them.
 */
data class LoadStopEntry(
    val locationId: String = "",
    val isNewLocation: Boolean = false,
    val newLocationName: String = "",
    val newLocationCity: String = "",
    val newLocationState: String = "",
    val locationError: String? = null,
) {
    val hasLocation: Boolean get() = isNewLocation || locationId.isNotEmpty()
}

/**
 * Backing state for the duplicate-load dialog: just the fields that change between runs of the same
 * load. Everything else copies verbatim from the source load being edited.
 */
data class DuplicateLoadFormState(
    val draftId: String = newLoadDraftId(),
    val pickupDate: LocalDate = todayDate(),
    val originalDate: LocalDate = todayDate(),
    val loadNumber: String = "",
    val po: String = "",
    val orderNumber: String = "",
    // Prefilled from the source load's rate; the user keeps or edits the amounts.
    val rate: LoadRateFormEntry? = null,
) {
    /** Setting the pickup date also moves the drop date to match; the reverse never happens. */
    fun withPickupDate(date: LocalDate): DuplicateLoadFormState = copy(pickupDate = date, originalDate = date)
}

data class LoadRateFormEntry(
    val parentRateId: String,
    val rateCategory: LoadRateCategory,
    val rateType: LoadRateType,
    val unit: LoadRateUnit?,
    val pickupLocationId: String = "",
    val dropLocationId: String = "",
    val autoFilled: Boolean = false,
    val flatRateValue: String = "",
    // Per-unit prices change load to load, so it's entered per load like the flat rate.
    val perUnitValue: String = "",
    val unitQuantity: String = "",
    // Entry convenience for weight-based units: never persisted, kept in sync with unitQuantity.
    val pounds: String = "",
    val otherCharge: String = "",
    val stopOffCharge: String = "",
    val detention: String = "",
    val fuelSurchargePercent: String = "",
) {
    fun toRateInfo(): LoadRateInfo = LoadRateInfo(
        parentRateId = parentRateId,
        rateCategory = rateCategory,
        rateType = rateType,
        unit = unit,
        perUnit = perUnit,
        pickupLocationId = pickupLocationId,
        dropLocationId = dropLocationId,
        flatRateValue = flatRateValue.dollarsToCents(),
        unitQuantity = unitQuantity.trim().replace(",", ".").toDoubleOrNull() ?: 0.0,
        otherCharge = otherCharge.dollarsToCents(),
        stopOffCharge = stopOffCharge.dollarsToCents(),
        detention = detention.dollarsToCents(),
        fuelSurchargePercent = fuelSurchargePercent.trim().replace(",", ".").toDoubleOrNull() ?: 0.0,
    )

    val perUnit: Int get() = perUnitValue.dollarsToCents()

    val total: Int get() = toRateInfo().total
    val totalFormatted: String get() = toRateInfo().totalFormatted
    val fuelAmountFormatted: String get() = toRateInfo().fuelSurchargeAmountFormatted

    /** Template label derived from the snapshot, so it works even if the template was deleted. */
    val snapshotLabel: String
        get() = toSnapshotTemplate().label

    fun routeLabel(locations: List<Location>): String = toSnapshotTemplate().routeLabel(locations)

    private fun toSnapshotTemplate(): LoadRate = LoadRate(
        id = parentRateId,
        rateCategory = rateCategory,
        rateType = rateType,
        unit = unit,
        perUnit = perUnit,
        pickupLocationId = pickupLocationId,
        dropLocationId = dropLocationId,
    )

    val perUnitFormatted: String get() = "$" + perUnit.formatCentsAsDollars()
    val unitLabel: String get() = unit?.displayName ?: "unit"

    val isWeightBased: Boolean get() = unit?.poundsPerUnit != null

    /** User typed pounds: keep their text, derive the unit quantity from it. */
    fun withPounds(text: String): LoadRateFormEntry {
        val lbsPerUnit = unit?.poundsPerUnit ?: return copy(pounds = text)
        val lbs = text.trim().replace(",", ".").toDoubleOrNull()
        return copy(
            pounds = text,
            unitQuantity = if (lbs == null) "" else (lbs / lbsPerUnit).toConvertedInput(),
        )
    }

    /** User typed the unit quantity: keep their text, derive pounds for weight-based units. */
    fun withUnitQuantity(text: String): LoadRateFormEntry {
        val lbsPerUnit = unit?.poundsPerUnit ?: return copy(unitQuantity = text)
        val quantity = text.trim().replace(",", ".").toDoubleOrNull()
        return copy(
            unitQuantity = text,
            pounds = if (quantity == null) "" else (quantity * lbsPerUnit).toConvertedInput(),
        )
    }

    /**
     * After the unit may have changed (template re-selection): pounds is what the load ticket
     * says, so it wins when present; otherwise pounds is re-derived from the typed quantity.
     */
    fun reconciledWeightFields(): LoadRateFormEntry = when {
        !isWeightBased -> copy(pounds = "")
        pounds.isNotBlank() -> withPounds(pounds)
        else -> withUnitQuantity(unitQuantity)
    }

    companion object {
        /** Fresh entry from a template: snapshot the pricing, prefill the template's price. */
        fun fromTemplate(loadRate: LoadRate): LoadRateFormEntry = LoadRateFormEntry(
            parentRateId = loadRate.id,
            rateCategory = loadRate.rateCategory,
            rateType = loadRate.rateType,
            unit = loadRate.unit,
            pickupLocationId = loadRate.pickupLocationId,
            dropLocationId = loadRate.dropLocationId,
            flatRateValue = loadRate.flatRate.centsToDollarInput(),
            perUnitValue = loadRate.perUnit.centsToDollarInput(),
        )

        /** Seeds the form from a load's saved rate (edit mode and the duplicate dialog). */
        fun fromRateInfo(info: LoadRateInfo): LoadRateFormEntry = LoadRateFormEntry(
            parentRateId = info.parentRateId,
            rateCategory = info.rateCategory,
            rateType = info.rateType,
            unit = info.unit,
            pickupLocationId = info.pickupLocationId,
            dropLocationId = info.dropLocationId,
            flatRateValue = info.flatRateValue.centsToDollarInput(),
            perUnitValue = info.perUnit.centsToDollarInput(),
            unitQuantity = info.unitQuantity.toInputText(),
            pounds = info.unit?.poundsPerUnit?.let { (info.unitQuantity * it).toConvertedInput() } ?: "",
            otherCharge = info.otherCharge.centsToDollarInput(),
            stopOffCharge = info.stopOffCharge.centsToDollarInput(),
            detention = info.detention.centsToDollarInput(),
            fuelSurchargePercent = info.fuelSurchargePercent.toInputText(),
        )

        // Drop a trailing ".0" so whole values read as typed ("412", not "412.0").
        private fun Double.toInputText(): String =
            if (this == 0.0) "" else toString().removeSuffix(".0")

        // Round to 2 decimals and build the text from scaled longs — Double.toString
        // renders differently on JVM and JS.
        private fun Double.toConvertedInput(): String {
            val scaled = (this * 100).roundToLong()
            if (scaled == 0L) return ""
            val sign = if (scaled < 0) "-" else ""
            val whole = abs(scaled) / 100
            val frac = (abs(scaled) % 100).toInt()
            return when {
                frac == 0 -> "$sign$whole"
                frac % 10 == 0 -> "$sign$whole.${frac / 10}"
                else -> "$sign$whole.${frac.toString().padStart(2, '0')}"
            }
        }
    }
}

data class AddLoadFormState(
    val draftId: String = newLoadDraftId(),
    // Customer
    val customerId: String = "",
    val isNewCustomer: Boolean = false,
    val newCustomerContactName: String = "",
    val newCustomerCompanyName: String = "",
    val newCustomerAddress: String = "",
    val newCustomerPhone: String = "",
    val newCustomerEmail: String = "",
    val newCustomerPaymentTerms: String = "",
    val newCustomerNotes: String = "",
    // The load's pickup date and drop date ([originalDate]).
    val pickupDate: LocalDate = todayDate(),
    val originalDate: LocalDate = todayDate(),
    // Stops — at least one pickup and one drop
    val pickups: List<LoadStopEntry> = listOf(LoadStopEntry()),
    val drops: List<LoadStopEntry> = listOf(LoadStopEntry()),
    // Driver — optional in general, but required for after-the-fact (past-dated) loads.
    val employeeId: String = "",
    // Blank means "match from the mileage log"; set only to override a missing or wrong match.
    val truckId: String = "",
    // Shared details
    val loadNumber: String = "",
    val po: String = "",
    val orderNumber: String = "",
    val commodity: String = "",
    val customerRequirements: String = "",
    // Status flags
    val setUp: Boolean = false,
    val ready: Boolean = false,
    // Optional pricing — admin-only in the UI, but always carried so non-admin edits preserve it.
    val rate: LoadRateFormEntry? = null,
    // Uploaded documents kept with the load. Files upload as soon as they're picked;
    // the resulting name/url pairs are saved with the load.
    val attachments: List<LoadAttachment> = emptyList(),
    val isUploadingAttachment: Boolean = false,
    val attachmentError: String? = null,
    // Validation
    val customerError: String? = null,
    val driverError: String? = null,
) {
    val hasCustomer: Boolean get() = isNewCustomer || customerId.isNotEmpty()
    val hasAllPickupLocations: Boolean get() = pickups.all { it.hasLocation }
    val hasAllDropLocations: Boolean get() = drops.all { it.hasLocation }
    val hasDriver: Boolean get() = employeeId.isNotEmpty()

    val stopPair: Pair<String, String>?
        get() {
            val pickup = pickups.firstOrNull()?.takeIf { !it.isNewLocation }?.locationId.orEmpty()
            val drop = drops.firstOrNull()?.takeIf { !it.isNewLocation }?.locationId.orEmpty()
            return if (pickup.isEmpty() || drop.isEmpty()) null else pickup to drop
        }

    /** A past load date means the load already happened; the driver must be recorded. */
    fun isPast(today: LocalDate): Boolean = originalDate < today

    /** Setting the pickup date also moves the drop date to match; the reverse never happens. */
    fun withPickupDate(date: LocalDate): AddLoadFormState = copy(pickupDate = date, originalDate = date)
}
